// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsLeftMenuActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { compose, withHandlers, withState, withProps } from 'recompose';
import uiState from 'logic/Izenda.UiState';
import { curry, remove, clone, forEach, filter, isNil } from 'lodash/fp';

export default compose(
  withProps({
    getLeftMenu
  }),
  withHandlers({
    changeActiveKey: curry(changeActiveKey),
    updateActiveLink: curry(updateActiveLink)
  }),
  withState('menu', 'updateMenu', props => getLeftMenu.call(props))
);

/**
 * Get left menu for settings page
 */
function getLeftMenu(): Array<any> {
  const { store: { contextMenu: { contextMenu } }, hasSetupDatabase, authorization } = this;

  // For first time , not setup data base : Only DB & License is shown
  if (!hasSetupDatabase) {
    return filter({ key: 'setupAndLicense' }, contextMenu);
  }

  const menu = [];
  forEach(link => {
    if (link.items) {
      const newChild = remove(
        child => !checkPrivilege(child, authorization) || !checkLicense(child, authorization),
        link.items
      );
      if (newChild.length > 0) {
        const newLink = clone(link);
        newLink.items = newChild;
        menu.push(newLink);
      }
    } else {
      if (
        checkLicense(link, authorization) &&
        checkPrivilege(link, authorization) &&
        !checkIsIntegration(link, authorization)
      ) {
        menu.push(link);
      }
    }
  }, contextMenu);

  return menu;
}

/**
 * Check privilege
 * @param {Object} link
 */
function checkPrivilege(
  { forSystemAdmin, forSystemUser, forMultipleTenant, permissionKeys, permissionOperator },
  authorization: any
): boolean {
  const { isSingleTenantMode, currentUser: { systemAdmin, isSystemUser } } = uiState;

  if (!authorization.isCompleteLoad) return false;
  if (forSystemAdmin && !systemAdmin) return false;
  if (forSystemUser && !isSystemUser) return false;
  if (forMultipleTenant && !isSingleTenantMode) return false;

  if (permissionKeys) {
    if (permissionOperator) {
      if (!authorization.checkPrivileges(permissionKeys, permissionOperator)) {
        return false;
      }
    } else {
      if (!authorization.checkPrivileges(permissionKeys)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Check license
 * @param {Object} link
 */
function checkLicense({ licenseKey }, authorization: any): boolean {
  return !licenseKey || authorization.checkLicense(licenseKey);
}

/**
 * Check the system is running under integration mode
 * @param {Object} link
 */
function checkIsIntegration({ integrationCheck }, authorization: any): boolean {
  return integrationCheck && authorization.isIntegrationMode();
}

/**
 * Change active key
 * @param {Object} props 
 * @param {Object} newLink 
 */
function changeActiveKey(props: any, newLink: any): void {
  const { store: settingStore, store: { contextMenu } } = props;
  if (contextMenu.activeLink !== newLink) {
    props.confirmDirtyForm(uiState.isDirty, { onConfirmDirtyForm: uiState.onOkDirtyForm }).then(() => {
      settingStore.checkSettingLevel(newLink);
      contextMenu.changeActiveLink(newLink);
    });
  }
}

/**
 * Update active link
 * @param {Object} props 
 * @param {Object} link 
 */
function updateActiveLink(props: any, link: any): void {
  if (link) {
    props.store.contextMenu.contextMenuActiveLink = link.key;
    props.store.contextMenu.activeLink = isNil(link.items) ? link.key : link.items[0].key;
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/Izenda.SettingsLeftMenuActions.js